import axios, {AxiosError, InternalAxiosRequestConfig} from 'axios';

const ID_TOKEN_KEY = 'id_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// @ts-ignore
const BASE_URL = import.meta.env.VITE_API_BASE_URL;

const instance = axios.create({baseURL: BASE_URL});
const refreshClient = axios.create({baseURL: BASE_URL});

let idToken: string | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

function getStoredIdToken(): string | null {
    try {
        return localStorage.getItem(ID_TOKEN_KEY);
    } catch {
        return null;
    }
}

function getStoredRefreshToken(): string | null {
    try {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
        return null;
    }
}

function setStoredTokens(tokens: { idToken: string | null; refreshToken?: string | null }) {
    try {
        if (tokens.idToken) localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
        else localStorage.removeItem(ID_TOKEN_KEY);

        if (typeof tokens.refreshToken !== 'undefined') {
            if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            else localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
    } catch {
    }
}

async function refreshIdToken(): Promise<string | null> {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) return null;

    try {
        const {data} = await refreshClient.post('/api/v1/auth/refresh', {refreshToken});
        const newIdToken: string | null = data?.idToken ?? null;
        const newRefresh: string | null = data?.refreshToken ?? null;

        idToken = newIdToken;
        setStoredTokens({idToken: newIdToken, refreshToken: newRefresh ?? undefined});
        return newIdToken;
    } catch {
        idToken = null;
        setStoredTokens({idToken: null, refreshToken: null});
        return null;
    }
}

// Attach token
instance.interceptors.request.use((c: InternalAxiosRequestConfig) => {
    const tok = idToken ?? getStoredIdToken();
    if (tok) {
        // @ts-ignore
        c.headers = c.headers ?? {};
        (c.headers as any).Authorization = `Bearer ${tok}`;
    }
    return c;
});

// Refresh on 401
instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
        const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
        const status = error.response?.status;

        if (!original || status !== 401 || original._retry) throw error;
        original._retry = true;

        try {
            if (!isRefreshing) {
                isRefreshing = true;
                refreshPromise = refreshIdToken().finally(() => {
                    isRefreshing = false;
                });
            }
            const newToken = await (refreshPromise as Promise<string | null>);
            if (newToken) {
                // @ts-ignore
                original.headers = original.headers ?? {};
                (original.headers as any).Authorization = `Bearer ${newToken}`;
                return instance(original);
            }
        } catch (e) {
            throw e;
        }
    }
);

export default {
    get: instance.get,
    post: instance.post,
    put: instance.put,
    patch: instance.patch,
    delete: instance.delete,
    setTokens(idTok: string | null, refreshTok: string | null) {
        idToken = idTok;
        setStoredTokens({idToken: idTok, refreshToken: refreshTok});
    },
    clearTokens() {
        idToken = null;
        setStoredTokens({idToken: null, refreshToken: null});
    },
};
